import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Plus, Coins } from 'lucide-react';
import { SpecialPaymentDto, specialPaymentsApi, handleApiError } from '../../services/specialPaymentsService';
import SpecialPaymentsTable from '../specialpayments/SpecialPaymentsTable';
import CreateSpecialPaymentModal from '../modals/CreateSpecialPaymentModal';
import UpdateSpecialPaymentModal from '../modals/UpdateSpecialPaymentModal';
import MarkAsPaidModal from '../modals/MarkAsPaidModal';

interface SpecialPaymentsTabProps {
  clientId: number;
  clientName: string;
}

const Container = styled.div`
  direction: rtl;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  /* No background - unified canvas */

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    justify-content: center;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 16px;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid #dc2626;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  /* No box styling - unified canvas */
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 24px;
  color: var(--text-secondary);
  opacity: 0.3;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 600;
  color: var(--text-primary);
`;

const EmptyStateMessage = styled.p`
  margin: 0 0 28px 0;
  font-size: 14px;
  color: var(--text-secondary);
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ConfirmContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 450px;
  padding: 24px;
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
`;

const ConfirmMessage = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConfirmButton = styled.button<{ variant?: 'danger' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: #dc2626;
          color: white;
          &:hover { filter: brightness(0.9); }
        `;
      default:
        return `
          background: var(--border-color);
          color: var(--text-primary);
          &:hover { filter: brightness(0.95); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SpecialPaymentsTab: React.FC<SpecialPaymentsTabProps> = ({ clientId, clientName }) => {
  const [payments, setPayments] = useState<SpecialPaymentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<SpecialPaymentDto | null>(null);
  const [paymentToMarkAsPaid, setPaymentToMarkAsPaid] = useState<SpecialPaymentDto | null>(null);
  const [paymentToCancel, setPaymentToCancel] = useState<SpecialPaymentDto | null>(null);

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await specialPaymentsApi.getByClientId(clientId);
      setPayments(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleEdit = (payment: SpecialPaymentDto) => {
    setSelectedPayment(payment);
    setShowUpdateModal(true);
  };

  const handleMarkAsPaid = (payment: SpecialPaymentDto) => {
    setPaymentToMarkAsPaid(payment);
    setShowMarkAsPaidModal(true);
  };

  const handleCancelRequest = (payment: SpecialPaymentDto) => {
    setPaymentToCancel(payment);
  };

  const handleCancelConfirm = async () => {
    if (!paymentToCancel) return;

    try {
      await specialPaymentsApi.delete(paymentToCancel.id);
      setPaymentToCancel(null);
      loadPayments();
    } catch (err) {
      toast.error(handleApiError(err));
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>טוען תשלומים מיוחדים...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <Button onClick={loadPayments}>נסה שנית</Button>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>תשלומים מיוחדים</Title>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus size={16} strokeWidth={2} />
          הוסף תשלום מיוחד
        </Button>
      </Header>

      {payments.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>
            <Coins size={72} strokeWidth={1.5} />
          </EmptyStateIcon>
          <EmptyStateTitle>אין תשלומים מיוחדים</EmptyStateTitle>
          <EmptyStateMessage>
            כאן תוכל לעקוב אחר תשלומים מיוחדים כמו שכר טרחה עורך דין, חוות דעת רפואיות,
            ועוד. תשלומים אלו אינם קשורים למערכת החיוב הרגילה.
          </EmptyStateMessage>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus size={16} strokeWidth={2} />
            הוסף תשלום מיוחד ראשון
          </Button>
        </EmptyState>
      ) : (
        <SpecialPaymentsTable
          payments={payments}
          onEdit={handleEdit}
          onMarkAsPaid={handleMarkAsPaid}
          onCancel={handleCancelRequest}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSpecialPaymentModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadPayments();
          }}
          clientId={clientId}
          clientName={clientName}
        />
      )}

      {/* Update Modal */}
      {showUpdateModal && (
        <UpdateSpecialPaymentModal
          isOpen={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedPayment(null);
          }}
          onSuccess={() => {
            setShowUpdateModal(false);
            setSelectedPayment(null);
            loadPayments();
          }}
          payment={selectedPayment}
        />
      )}

      {/* Mark as Paid Modal */}
      {showMarkAsPaidModal && (
        <MarkAsPaidModal
          isOpen={showMarkAsPaidModal}
          onClose={() => {
            setShowMarkAsPaidModal(false);
            setPaymentToMarkAsPaid(null);
          }}
          onSuccess={() => {
            setShowMarkAsPaidModal(false);
            setPaymentToMarkAsPaid(null);
            loadPayments();
          }}
          payment={paymentToMarkAsPaid}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      {paymentToCancel && (
        <ConfirmDialog>
          <ConfirmContent>
            <ConfirmTitle>ביטול תשלום מיוחד</ConfirmTitle>
            <ConfirmMessage>
              האם אתה בטוח שברצונך לבטל את התשלום "{paymentToCancel.description}"?
              <br /><br />
              פעולה זו תסמן את התשלום כבוטל אך לא תמחק אותו מהמערכת.
            </ConfirmMessage>
            <ConfirmButtons>
              <ConfirmButton onClick={() => setPaymentToCancel(null)}>
                ביטול
              </ConfirmButton>
              <ConfirmButton variant="danger" onClick={handleCancelConfirm}>
                אישור ביטול התשלום
              </ConfirmButton>
            </ConfirmButtons>
          </ConfirmContent>
        </ConfirmDialog>
      )}
    </Container>
  );
};

export default SpecialPaymentsTab;
